export function drawMultiGradientCircleCanvas({
  canvas = document.createElement('canvas'),
  data,
  gap = 10, // 像素
  maxValue,
  gradient = {
    0: 'rgb(50, 250, 56)',
    0.5: 'rgb(0, 0, 255)',
    1: 'rgb(250, 50, 56)'
  }
}) {
  if (!data || data.length === 0)
    return {
      canvas,
      bounds: [
        [0, 0],
        [0, 0]
      ]
    };

  // 直接用BD09经纬度坐标，不做坐标系转换
  // 计算经纬度包围盒
  let minLng = Infinity,
    minLat = Infinity,
    maxLng = -Infinity,
    maxLat = -Infinity;
  data.forEach(item => {
    const [lng, lat] = item.coordinate;
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  });

  // 经纬度范围
  let lngRange = maxLng - minLng;
  let latRange = maxLat - minLat;

  // 单节点或范围为0时，扩展一个默认范围使节点居中显示
  const defaultRange = 0.0005; // 约1km
  if (lngRange === 0) {
    minLng -= defaultRange / 2;
    maxLng += defaultRange / 2;
    lngRange = defaultRange;
  }
  if (latRange === 0) {
    minLat -= defaultRange / 2;
    maxLat += defaultRange / 2;
    latRange = defaultRange;
  }

  // 找出最大半径（像素），用于确保画布包含完整的圆
  const maxRadius = Math.max(...data.map(item => item.radius || 0));
  const maxCircleSize = maxRadius * 2;

  // 纬度修正：1度经度在不同纬度对应的实际距离不同
  // 1度纬度 ≈ 111km，1度经度 ≈ 111km * cos(纬度)
  // 为了让圆不变形，需要将经度乘以cos(纬度)修正
  const centerLat = (minLat + maxLat) / 2;
  const cosLat = Math.cos((centerLat * Math.PI) / 180);
  // 修正后的经度范围（等效于纬度方向的距离）
  const correctedLngRange = lngRange * cosLat;

  // 经纬度转像素的缩放比例，用修正后的范围计算
  // 目标画布像素大小约为600px
  const targetSize = 600;
  const lngScale = correctedLngRange > 0 ? targetSize / correctedLngRange : 1;
  const latScale = latRange > 0 ? targetSize / latRange : 1;
  const scale = Math.min(lngScale, latScale);

  // 限制缩放范围
  const finalScale = Math.max(0.01, Math.min(scale, 100000));

  // 画布尺寸：用修正后的经度范围计算宽度，保证X/Y方向实际距离与像素比例一致
  const canvasWidth = Math.ceil(correctedLngRange * finalScale + gap * 2 + maxCircleSize);
  const canvasHeight = Math.ceil(latRange * finalScale + gap * 2 + maxCircleSize);
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  const { _gradCanvas, gradData } = createGradient(gradient);

  // 缓存不同半径的圆形canvas
  const radCanvasMap = {};

  data.forEach(item => {
    const radius = item.radius || 0;
    if (radius <= 0) return;

    const [lng, lat] = item.coordinate;

    // createRadius创建的canvas尺寸为 2 * fullRadius，其中 fullRadius = radius + blurRadius = radius * 2
    const fullRadius = radius * 2;
    const R = fullRadius * 2; // canvas宽度 = 2 * fullRadius

    let radCanvas, radCtx, radImgData;

    if (radCanvasMap[R]) {
      ({ radCanvas, radCtx, radImgData } = radCanvasMap[R]);
    } else {
      radCanvas = createRadius(radius, radius);
      radCtx = radCanvas.getContext('2d', { willReadFrequently: true });
      radImgData = radCtx.getImageData(0, 0, R, R);
      radCanvasMap[R] = { radCanvas, radCtx, radImgData };
    }

    const { value } = item;

    // 计算点在画布上的中心位置
    // x: 经度从左边界(minLng)偏移，乘以cosLat修正 + gap + 半径偏移
    // y: 纬度从上边界(maxLat)偏移（纬度越大越靠上，画布Y轴向下）+ gap + 半径偏移
    const centerX = (lng - minLng) * cosLat * finalScale + gap + maxCircleSize / 2;
    const centerY = (maxLat - lat) * finalScale + gap + maxCircleSize / 2;

    // getImageData的起点是矩形左上角，需要从中点减去半边长
    const x = Math.round(centerX - R / 2);
    const y = Math.round(centerY - R / 2);

    // 获取画布指定大小的图像数据
    const imgData = ctx.getImageData(x, y, R, R);
    mixImgData(imgData, radImgData, value / maxValue);
    ctx.putImageData(imgData, x, y);
  });

  const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);

  colorize(imgData.data, gradData);
  ctx.putImageData(imgData, 0, 0);

  ctx.globalAlpha = 1;
  // ctx.fillStyle = gradCanvas;
  // ctx.fillRect(canvas.width - 40, 0, 30, 256);

  // 计算bounds：BMapGL.Bounds需要[西南角(左下), 东北角(右上)]
  // canvas像素边距转换为经纬度偏移
  // 经度方向需要除以cosLat还原为经度单位
  const lngOffset = (gap + maxCircleSize / 2) / (finalScale * cosLat);
  const latOffset = (gap + maxCircleSize / 2) / finalScale;

  // 西南角：经度最小、纬度最小
  // 东北角：经度最大、纬度最大
  const bounds = [
    [minLng - lngOffset, minLat - latOffset],
    [maxLng + lngOffset, maxLat + latOffset]
  ];

  return { canvas, bounds };
}

export function makeGradientCircleCanvas({
  canvas = document.createElement('canvas'),
  width,
  height,
  max,
  value,
  gradientMap = {
    0: 'rgb(50, 250, 56)',
    0.5: 'rgb(0, 0, 255)',
    1: 'rgb(250, 50, 56)'
  }
}) {
  const w = width;
  const h = height;

  const data = [
    {
      x: width / 2,
      y: height / 2,
      value: value
    }
  ];

  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  // 创建一个线性渐变
  // 渐变起点在 x=20、y=0
  // 渐变终点在 x=220、y=0
  const { gradient, gradData } = createGradient(gradientMap);

  // 设置填充样式并绘制矩形
  // ctx.fillStyle = gradient;
  // ctx.fillRect(0,0,100,256);

  const r = Math.min(width, height) / 3.5;
  const blur_r = Math.min(width, height) / 4;
  let R = (r + blur_r) * 2;

  const _radius = radius(r, blur_r);
  const _radCtx = _radius.getContext('2d', { willReadFrequently: true });
  const radImgData = _radCtx.getImageData(0, 0, R, R);

  // 获取画布指定大小的图像数据
  data.forEach(item => {
    let { x, y, value } = item;

    x = x - R / 2;
    y = y - R / 2;

    const imgData = ctx.getImageData(x, y, R, R);
    mixImgData(imgData, radImgData, value / max);
    ctx.putImageData(imgData, x, y);
    // ctx.drawImage(_radius, x, y);
  });

  // const imgDataA = ctx.getImageData(0, 0, R, R);

  // mixImgData2(imgDataA.data, data, 1);
  // ctx.putImageData(imgDataA, 0, 0);

  const imgData = ctx.getImageData(0, 0, w, h);

  colorize(imgData.data, gradData);
  ctx.putImageData(imgData, 0, 0);

  ctx.globalAlpha = 1;
  ctx.fillStyle = gradient;
  ctx.fillRect(470, 0, 30, 256);

  // 检测渐变值高低进行颜色混合
  function mixImgData(a, b, opacity) {
    const aData = a.data;
    const bData = b.data;

    const len = aData.length;
    for (let i = 0; i < len; i += 4) {
      if (!aData[i + 3] && !bData[i + 3]) continue;

      // 计算混合权重
      const alpha = bData[i + 3] / 255;
      const mixAlpha = alpha * opacity;

      if (aData[i + 3]) {
        if (!bData[i + 3]) continue;

        const currentAlpha = aData[i + 3] / 255;
        const newAlpha = Math.sqrt(currentAlpha * currentAlpha + mixAlpha * mixAlpha);

        // const maxAlpha = Math.max(currentAlpha, mixAlpha);

        aData[i + 3] = Math.min(newAlpha * 255, 255);

        // if (maxAlpha > 0.9) {
        //   aData[i + 3] = maxAlpha * 255;
        //   continue;
        // }

        // aData[i + 3] = Math.min(newAlpha * 255, 255);

        // aData[i + 3] = Math.max(currentAlpha * 255, mixAlpha * 255);
      } else {
        aData[i + 3] = mixAlpha * 255;
      }
    }
  }

  // function mixImgData2(a, b, opacity) {
  //   const len = a.length;
  //   for (let i = 0; i < len; i += 4) {
  //     a[i + 3] = b[i + 3];
  //   }
  // }

  function createGradient(e) {
    let t = document.createElement('canvas');
    let i = t.getContext('2d');
    let n = i.createLinearGradient(0, 0, 0, 256);

    t.width = 1;
    t.height = 256;

    for (var s in e) {
      n.addColorStop(+s, e[s]);
    }

    i.fillStyle = n;
    i.fillRect(0, 0, 1, 256);

    return { gradient: n, gradData: i.getImageData(0, 0, 1, 256).data };
  }

  function radius(r, blur_r) {
    // 设置模糊半径，默认值为15
    blur_r = void 0 === blur_r ? 15 : blur_r;

    // 创建一个新的canvas元素并保存到this._circle
    var i = document.createElement('canvas');
    // 获取canvas的2d上下文
    const ctx = i.getContext('2d');
    // 计算总半径（原始半径 + 模糊半径）并保存到this._r
    const a_r = r + blur_r;

    // 设置canvas的宽高为总半径的2倍
    i.width = i.height = 2 * a_r;
    // 设置阴影偏移量为总半径的2倍
    ctx.shadowOffsetX = ctx.shadowOffsetY = 2 * a_r;
    // 设置阴影模糊度
    ctx.shadowBlur = blur_r;
    // 设置阴影颜色为黑色
    ctx.shadowColor = 'black';
    // 开始绘制路径
    ctx.beginPath();
    // 绘制圆弧，圆心位于(-s, -s)，半径为e，从0到2π
    ctx.arc(-a_r, -a_r, r, 0, 2 * Math.PI, !0);
    // 关闭路径
    ctx.closePath();
    // 填充路径
    ctx.fill();

    return i;
  }

  function colorize(e, t) {
    for (var i, n = 0, s = e.length; n < s; n += 4) {
      i = 4 * e[n + 3];

      if (i) {
        // console.log(
        //   `%c颜色-rgba(${t[i]}, ${t[i + 1]}, ${t[i + 2]}, ${e[n + 3]})`,
        //   `color: rgba(${t[i]}, ${t[i + 1]}, ${t[i + 2]}, ${e[n + 3]})`
        // );

        e[n] = t[i];
        e[n + 1] = t[i + 1];
        e[n + 2] = t[i + 2];
      }
    }
  }

  return canvas;
}

function createGradient(gradientMap) {
  let canvas = document.createElement('canvas');
  let ctx = canvas.getContext('2d');
  let gradient = ctx.createLinearGradient(0, 0, 0, 256);

  canvas.width = 1;
  canvas.height = 256;

  for (var key in gradientMap) {
    gradient.addColorStop(+key, gradientMap[key]);
  }

  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1, 256);

  return {
    gradCanvas: gradient,
    gradData: ctx.getImageData(0, 0, 1, 256).data
  };
}

function createRadius(radius, blurRadius) {
  // 设置模糊半径，默认值为15
  blurRadius = void 0 === blurRadius ? 15 : blurRadius;

  // 创建一个新的canvas元素并保存到this._circle
  var canvas = document.createElement('canvas');
  // 获取canvas的2d上下文
  const ctx = canvas.getContext('2d');
  // 计算总半径（原始半径 + 模糊半径）并保存到this._r
  const fullRadius = radius + blurRadius;

  // 设置canvas的宽高为总半径的2倍
  canvas.width = canvas.height = 2 * fullRadius;
  // 设置阴影偏移量为总半径的2倍
  ctx.shadowOffsetX = ctx.shadowOffsetY = 2 * fullRadius;
  // 设置阴影模糊度
  ctx.shadowBlur = blurRadius;
  // 设置阴影颜色为黑色
  ctx.shadowColor = 'black';
  // 开始绘制路径
  ctx.beginPath();
  // 绘制圆弧，圆心位于(-s, -s)，半径为e，从0到2π
  ctx.arc(-fullRadius, -fullRadius, radius, 0, 2 * Math.PI, !0);
  // 关闭路径
  ctx.closePath();
  // 填充路径
  ctx.fill();

  return canvas;
}

function mixImgData(aImageData, bImageData, opacity) {
  const aData = aImageData.data;
  const bData = bImageData.data;

  const len = aData.length;
  for (let i = 0; i < len; i += 4) {
    if (!aData[i + 3] && !bData[i + 3]) continue;

    // 计算混合权重
    const alpha = bData[i + 3] / 255;
    const mixAlpha = alpha * opacity;

    if (aData[i + 3]) {
      if (!bData[i + 3]) continue;

      // 使用平方根混合，让过渡更加柔和
      const currentAlpha = aData[i + 3] / 255;
      const newAlpha = Math.sqrt(currentAlpha * currentAlpha + mixAlpha * mixAlpha);

      // const maxAlpha = Math.max(currentAlpha, mixAlpha);

      aData[i + 3] = Math.min(newAlpha * 255, 255);

      // if (maxAlpha > 0.9) {
      //   aData[i + 3] = maxAlpha * 255;
      //   continue;
      // }

      // aData[i + 3] = Math.min(newAlpha * 255, 255);

      // aData[i + 3] = Math.max(currentAlpha * 255, mixAlpha * 255);
    } else {
      aData[i + 3] = mixAlpha * 255;
    }
  }
}

function colorize(imageData, gradImageData) {
  for (var i, j = 0, len = imageData.length; j < len; j += 4) {
    i = 4 * imageData[j + 3];

    if (i) {
      // console.log(
      //   `%c颜色-rgba(${t[i]}, ${t[i + 1]}, ${t[i + 2]}, ${e[n + 3]})`,
      //   `color: rgba(${t[i]}, ${t[i + 1]}, ${t[i + 2]}, ${e[n + 3]})`
      // );

      imageData[j] = gradImageData[i];
      imageData[j + 1] = gradImageData[i + 1];
      imageData[j + 2] = gradImageData[i + 2];
    }
  }
}
